import { action, computed, observable } from 'mobx'
import { func, type IValidationSchema, validationContext } from 'validx'
import { type RynoType, Types } from '@/features/legends/editor/ryno-types'
import {
  QuestionEditorViewModelBase,
  type QuestionEditorViewModelOptions,
} from '@/features/legends/questions/QuestionEditorViewModelBase'
import type { VariableQuestion } from '@/features/legends/questions/questions'
import { RynoEditorViewModel } from '@/features/legends/ryno/RynoEditorViewModel'

/**
 * A typed value that formulas and effects can read and change.
 */
export class VariableQuestionEditorViewModel extends QuestionEditorViewModelBase<VariableQuestion> {
  private readonly _schema: IValidationSchema<VariableQuestionEditorViewModel> =
    {
      ...QuestionEditorViewModelBase.sharedSchema,
      // Listed so that changing the type revalidates the formula.
      variableValueType: [],
      expr: [
        func(
          () => (this.expr.value ? this.expr.analyze() : true),
          'There are formula errors.',
        ),
      ],
    }

  readonly type = 'Variable'

  /**
   * The initial value. Empty means the type's default value.
   */
  readonly expr: RynoEditorViewModel

  @observable accessor label = ''

  /**
   * The variable's type; `valueType` in the legend document.
   */
  @observable.ref accessor variableValueType: RynoType = Types.number

  readonly validation = validationContext(this, this._schema)

  constructor(options: QuestionEditorViewModelOptions) {
    super(options)
    this.expr = new RynoEditorViewModel({
      getSymbolTable: () => this.symbolTable,
      getNeededType: () => this.variableValueType,
    })
    this.lifecycle.manage(this.expr)
    this._validateWhileEditing(this._schema, {
      variableValueType: ['expr'],
    })
  }

  /** @inheritDoc */
  @computed
  get valueType() {
    return this.variableValueType
  }

  /** @inheritDoc */
  get displayText() {
    return this.label
  }

  /**
   * The symbols the formula can use.
   */
  @computed
  get symbolTable() {
    return this.dependencies.symbols.getSymbolTableFor(this)
  }

  /**
   * Sets the variable's type.
   */
  @action.bound
  setVariableValueType(type: RynoType) {
    this.variableValueType = type
  }

  /** @inheritDoc */
  @action
  hydrate(question: VariableQuestion) {
    this._hydrateShared(question)
    this.label = question.label ?? ''
    this.expr.setValue(question.expr)
    this.variableValueType = question.valueType
      ? Types.fromJS(question.valueType)
      : Types.number
  }

  /** @inheritDoc */
  protected _serialize(): VariableQuestion {
    return {
      ...this._serializeShared(),
      type: this.type,
      valueType: Types.toJS(this.variableValueType),
      label: this.label,
      expr: this.expr.toJSON(),
    }
  }
}
